
import HeroSection from '@/components/hero/HeroSection';
import HeroMetrics from '@/components/hero/HeroMetrics';
import Section from '@/components/common/Section';
import Card from '@/components/cards/Card';
import USPCard from '@/components/cards/USPCard';
import LogoStrip from '@/components/visuals/LogoStrip';
import Button from '@/components/common/Button';
import ChatbotModal from '@/components/chatbot/ChatbotModal';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';

const services = [
  {
    title: "Product Engineering",
    description: "Transform ideas into scalable platforms with end-to-end engineering excellence.",
    icon: <img src="/icons/Gear.gif" alt="Product Engineering" className="w-full h-full object-contain" />,
  },
  {
    title: "Enterprise Automation",
    description: "Modernize legacy systems with intelligent automation and workflow optimization.",
    icon: <img src="/icons/Artificially intelligent robot Cooperation.gif" alt="Enterprise Automation" className="w-full h-full object-contain" />,
  },
  {
    title: "Intelligent Systems",
    description: "Build AI-powered solutions that enhance decision-making and operational efficiency.",
    icon: <img src="/icons/Artificial Intelligence.gif" alt="Intelligent Systems" className="w-full h-full object-contain" />,
  },
];

const usps = [
  {
    title: "Residential Engineering Teams",
    description: "24×7 availability with high-speed execution model for consistent delivery.",
    image: "/images/usp-residential-teams.png",
  },
  {
    title: "Consultant Network",
    description: "1000+ experts for architecture and stabilization across diverse technologies.",
    image: "/images/usp-consultant-network.png",
  },
  {
    title: "Augmented Workforce",
    description: "Human expertise enhanced with GitHub Copilot and AI tools for maximum productivity.",
    image: "/images/usp-augmented-workforce.png",
  },
  {
    title: "Rapid Scale",
    description: "Scale from 10 to 100+ resources rapidly while maintaining quality standards.",
    image: "/images/usp-rapid-scale.png",
  },
];

const clientTypes = [
  {
    title: "OEM & Manufacturing",
    description: "Industrial automation and system integration",
    icon: <img src="/icons/Manufacturing.gif" alt="OEM & Manufacturing" className="w-full h-full object-contain" />,
  },
  {
    title: "Food & Agriculture",
    description: "Supply chain and processing optimization",
    icon: <img src="/icons/FIELD.gif" alt="Food & Agriculture" className="w-full h-full object-contain" />,
  },
  {
    title: "Enterprises",
    description: "Digital transformation and modernization",
    icon: <img src="/icons/business app.gif" alt="Enterprises" className="w-full h-full object-contain" />,
  },
  {
    title: "Product Companies",
    description: "Scalable platform development",
    icon: <img src="/icons/Growth business.gif" alt="Product Companies" className="w-full h-full object-contain" />,
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  
  return (
    <>
      {/* Hero Section */}
      <HeroSection onStartConversation={() => setIsChatbotOpen(true)} />
      
      {/* Trust Metrics */}
      <HeroMetrics />
      
      {/* What We Do */}
      <Section 
        title="What We Do"
        subtitle="End-to-end software development with architectural control and long-term ownership"
        centered
        background="surface"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <div key={index} className="h-full">
              <Card
                title={service.title}
                description={service.description}
                icon={service.icon}
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Why Ennobridge (USPs) */}
      <Section 
        title="Why Ennobridge"
        subtitle="Unique delivery model that scales with your ambitions"
        centered
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {usps.map((usp, index) => (
            <USPCard
              key={index}
              title={usp.title}
              description={usp.description}
              image={usp.image}
            />
          ))}
        </div>
      </Section>

      {/* Client Types */}
      <Section 
        title="Industries We Serve"
        subtitle="Diverse expertise across multiple industry verticals"
        centered
        background="surface"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {clientTypes.map((client, index) => (
            <div key={index} className="h-full">
              <Card
                title={client.title}
                description={client.description}
                icon={client.icon}
                compact
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Global Presence */}
      <Section>
        <LogoStrip 
          title="Global Presence" 
          logos={[
            { name: "Australia", icon: "🇦🇺" },
            { name: "Japan", icon: "🇯🇵" },
            { name: "US", icon: "🇺🇸" },
            { name: "India", icon: "🇮🇳" }
          ]}
        />
      </Section>

      {/* Primary CTA */}
      <Section centered background="gradient">
        <motion.div 
          className="text-center"
          
        >
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-textPrimary mb-6">
            Let's Build Something That Lasts
          </h2>
          <p className="text-xl text-textSecondary mb-8 max-w-2xl mx-auto">
            Transform your ideas into scalable solutions with our proven delivery model and expert teams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsChatbotOpen(true)}>
              Start a Conversation
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/services')}>
              View Our Work
            </Button>
          </div>
        </motion.div>
      </Section>

      {/* Chatbot Modal */}
      <ChatbotModal 
        isOpen={isChatbotOpen} 
        onClose={() => setIsChatbotOpen(false)} 
      />
    </>
  );
}